import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import reportsService, { 
  ReportsSummary, 
  CasesByType, 
  LawyerPerformance, 
  ReportPeriod,
  CustomDateRange
} from '../../services/reportsService';

// Updated interfaces based on API documentation
export interface TotalCases {
  value: number;
  change: number;
  change_type: 'increase' | 'decrease';
}

export interface TotalRevenue {
  value: number;
  change: number;
  change_type: 'increase' | 'decrease';
}

export interface ActiveClients {
  value: number;
  change: number;
  change_type: 'increase' | 'decrease';
}

export interface SuccessRate {
  value: number;
  change: number;
  change_type: 'increase' | 'decrease';
}

export interface Period {
  from: string;
  to: string;
  label: string;
}

export interface ReportsSummaryData {
  total_cases: TotalCases;
  active_cases: number;
  completed_cases: number;
  total_revenue: TotalRevenue;
  active_clients: ActiveClients;
  success_rate: SuccessRate;
  period: Period;
}

export interface CasesByTypeData {
  type: string;
  cases: number;
  distribution: number;
  revenue: number;
}

export interface LawyerPerformanceData {
  name: string;
  user_id: string;
  cases_count: number;
  completed_cases: number;
  total_time: number;
  avg_satisfaction: number;
  revenue: number;
}

interface ReportsState {
  summary: ReportsSummaryData | null;
  casesByType: CasesByTypeData[];
  lawyerPerformance: LawyerPerformanceData[];
  selectedPeriod: ReportPeriod;
  customDateRange: CustomDateRange;
  loading: {
    summary: boolean;
    casesByType: boolean;
    lawyerPerformance: boolean;
    export: boolean;
  };
  error: string | null;
}

const initialState: ReportsState = {
  summary: null,
  casesByType: [],
  lawyerPerformance: [],
  selectedPeriod: 'this_month',
  customDateRange: {
    from: '',
    to: ''
  },
  loading: {
    summary: false,
    casesByType: false,
    lawyerPerformance: false,
    export: false,
  },
  error: null,
};

// Async thunks
export const fetchReportsSummary = createAsyncThunk(
  'reports/fetchSummary',
  async ({ userId, period, customDateRange }: { userId: string; period: ReportPeriod; customDateRange?: CustomDateRange }, { rejectWithValue }) => {
    try {
      const response = await reportsService.getSummary(userId, period, customDateRange);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch reports summary');
    }
  }
);

export const fetchCasesByType = createAsyncThunk(
  'reports/fetchCasesByType',
  async ({ userId, period, customDateRange }: { userId: string; period: ReportPeriod; customDateRange?: CustomDateRange }, { rejectWithValue }) => {
    try {
      const response = await reportsService.getCasesByType(userId, period, customDateRange);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch cases by type');
    }
  }
);

export const fetchLawyerPerformance = createAsyncThunk(
  'reports/fetchLawyerPerformance',
  async ({ userId, period, customDateRange }: { userId: string; period: ReportPeriod; customDateRange?: CustomDateRange }, { rejectWithValue }) => {
    try {
      const response = await reportsService.getLawyerPerformance(userId, period, customDateRange);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch lawyer performance');
    }
  }
);

export const exportPdfReport = createAsyncThunk(
  'reports/exportPdf',
  async ({ userId, period, customDateRange }: { userId: string; period: ReportPeriod; customDateRange?: CustomDateRange }, { rejectWithValue }) => {
    try {
      const blob = await reportsService.exportPdf(userId, period, customDateRange);
      // Create download link
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const fileName = period === 'custom' && customDateRange 
        ? `report_${customDateRange.from}_to_${customDateRange.to}.pdf`
        : `report_${period}_${new Date().toISOString().split('T')[0]}.pdf`;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      return true;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to export PDF report');
    }
  }
);

export const exportExcelReport = createAsyncThunk(
  'reports/exportExcel',
  async ({ userId, period, customDateRange }: { userId: string; period: ReportPeriod; customDateRange?: CustomDateRange }, { rejectWithValue }) => {
    try {
      const blob = await reportsService.exportExcel(userId, period, customDateRange);
      // Create download link
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const fileName = period === 'custom' && customDateRange 
        ? `cases_report_${customDateRange.from}_to_${customDateRange.to}.xlsx`
        : `cases_report_${period}_${new Date().toISOString().split('T')[0]}.xlsx`;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      return true;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to export Excel report');
    }
  }
);

const reportsSlice = createSlice({
  name: 'reports',
  initialState,
  reducers: {
    setSelectedPeriod: (state, action) => {
      state.selectedPeriod = action.payload;
    },
    setCustomDateRange: (state, action) => {
      state.customDateRange = action.payload;
    },
    clearError: (state) => {
      state.error = null;
    },
    resetReports: (state) => {
      state.summary = null;
      state.casesByType = [];
      state.lawyerPerformance = [];
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch summary
      .addCase(fetchReportsSummary.pending, (state) => {
        state.loading.summary = true;
        state.error = null;
      })
      .addCase(fetchReportsSummary.fulfilled, (state, action) => {
        state.loading.summary = false;
        state.summary = action.payload;
        state.error = null;
      })
      .addCase(fetchReportsSummary.rejected, (state, action) => {
        state.loading.summary = false;
        state.error = action.payload as string;
      })
      
      // Fetch cases by type
      .addCase(fetchCasesByType.pending, (state) => {
        state.loading.casesByType = true;
        state.error = null;
      })
      .addCase(fetchCasesByType.fulfilled, (state, action) => {
        state.loading.casesByType = false;
        state.casesByType = action.payload;
        state.error = null;
      })
      .addCase(fetchCasesByType.rejected, (state, action) => {
        state.loading.casesByType = false;
        state.error = action.payload as string;
      })
      
      // Fetch lawyer performance
      .addCase(fetchLawyerPerformance.pending, (state) => {
        state.loading.lawyerPerformance = true;
        state.error = null;
      })
      .addCase(fetchLawyerPerformance.fulfilled, (state, action) => {
        state.loading.lawyerPerformance = false;
        state.lawyerPerformance = action.payload;
        state.error = null;
      })
      .addCase(fetchLawyerPerformance.rejected, (state, action) => {
        state.loading.lawyerPerformance = false;
        state.error = action.payload as string;
      })
      
      // Export PDF
      .addCase(exportPdfReport.pending, (state) => {
        state.loading.export = true;
        state.error = null;
      })
      .addCase(exportPdfReport.fulfilled, (state) => {
        state.loading.export = false;
        state.error = null;
      })
      .addCase(exportPdfReport.rejected, (state, action) => {
        state.loading.export = false;
        state.error = action.payload as string;
      })
      
      // Export Excel
      .addCase(exportExcelReport.pending, (state) => {
        state.loading.export = true;
        state.error = null;
      })
      .addCase(exportExcelReport.fulfilled, (state) => {
        state.loading.export = false;
        state.error = null;
      })
      .addCase(exportExcelReport.rejected, (state, action) => {
        state.loading.export = false;
        state.error = action.payload as string;
      });
  },
});

export const { setSelectedPeriod, setCustomDateRange, clearError, resetReports } = reportsSlice.actions;
export default reportsSlice.reducer;

// Selectors
export const selectReports = (state: { reports: ReportsState }) => state.reports;
export const selectReportsSummary = (state: { reports: ReportsState }) => state.reports.summary;
export const selectCasesByType = (state: { reports: ReportsState }) => state.reports.casesByType;
export const selectLawyerPerformance = (state: { reports: ReportsState }) => state.reports.lawyerPerformance;
export const selectReportsLoading = (state: { reports: ReportsState }) => state.reports.loading;
export const selectReportsError = (state: { reports: ReportsState }) => state.reports.error;
export const selectSelectedPeriod = (state: { reports: ReportsState }) => state.reports.selectedPeriod;
export const selectCustomDateRange = (state: { reports: ReportsState }) => state.reports.customDateRange;
